import { JsonSerializable } from "../Models/JsonSerializable";
import { LuxonDateTimeParser } from "../Utilities/DateAndTime/LuxonDateTimeParser";

class ObjectHelper {
    public static getUpdatedFields<TObject extends object>(
        oldObj: TObject,
        newObj: TObject,
        trimStrings: boolean = true
    ): Partial<TObject> {
        if (!newObj)
            return null;

        oldObj = oldObj ?? {} as TObject;

        const updatedFields = {};
        const activeField = "b2bcc89b-974b-4728-861c-f7ff0a3c5d4d"; // unique field name to detect circular references
        let keys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)]));

        if (newObj instanceof JsonSerializable) {
            keys = keys
                .filter(key => !newObj[JsonSerializable.excludedFieldsKey].contains(key))
                .filter(key => key !== JsonSerializable.excludedFieldsKey && key !== JsonSerializable.includedFieldsKey);
        }

        for (let key of keys) {
            // get values (change undefined to null so the field can get serialized)
            let oldValue = oldObj?.[key] ?? null;
            let newValue = newObj?.[key] ?? null;
            const stringifiedOldValue = JSON.stringify(oldValue);
            const stringifiedNewValue = JSON.stringify(newValue);

            // handle circular references
            if (newObj[activeField])
                return undefined;

            newObj[activeField] = true;

            // track updated fields
            if (typeof newValue === "string") {
                if (trimStrings) {
                    oldValue = oldValue?.trim();
                    newValue = newValue.trim();
                }

                if (oldValue !== newValue)
                    updatedFields[key] = newValue;
            }
            else if (Array.isArray(newValue)) {
                if (stringifiedOldValue !== stringifiedNewValue)
                    updatedFields[key] = JSON.parse(stringifiedNewValue); // parse JSON instead of using newValue in case nested objects extend JsonSerializable, so we can ignore excluded fields
            }
            else if (newValue instanceof Date) {
                if ((!oldValue && newValue)
                    || (oldValue && LuxonDateTimeParser.parse(oldValue).valueOf() !== LuxonDateTimeParser.parse(newValue)?.valueOf())
                )
                    updatedFields[key] = newValue;
            }
            else if (newValue && Object.keys(newValue).length > 0) {
                const updatedField = this.getUpdatedFields(oldValue, newValue, trimStrings);

                if (!this.isObjectEmpty(updatedField))
                    updatedFields[key] = updatedField;
            }
            else if (oldValue !== newValue && stringifiedOldValue !== "{}" && stringifiedNewValue !== "{}")
                updatedFields[key] = newValue;

            delete newObj[activeField];
        }

        return updatedFields;
    }

    public static equals(obj1: object, obj2: object, trimStrings: boolean = false): boolean {
        if (Array.isArray(obj1) && Array.isArray(obj2) && obj1.length !== obj2.length)
            return false;

        if ([obj1, obj2].filter(p => !p).length === 1)
            return false;

        return Object.keys(this.getUpdatedFields(obj1 ?? {}, obj2 ?? {}, trimStrings)).length === 0;
    }

    private static isObjectEmpty(obj: object): boolean {
        return obj && !Object.keys(obj).length && JSON.stringify(obj) === "{}";
    }
}

export { ObjectHelper };
